import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShoppingBag, Megaphone, Volume2, ShieldCheck, FileText, ChevronRight, Languages, User, Phone, Mail, Lock, Bike, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { Screen, TopBar, PrimaryButton, MobileInput } from './PhoneFrame';
import { DeleteAccountButton } from './CustomerHome';
import { AvatarPicker } from '../components/ImageUpload';
import { LanguageSwitch } from '../components/LanguageToggle';
import { PAGES } from '../pages/Legal';
import { isMuted, setMuted } from '../lib/chime';
import { APP_VERSION } from '../lib/constants';

const PREFS_KEY = 'd7-notif-prefs';
const DEFAULT_PREFS = { orders: true, driver: true, promos: true };

export const notifPrefs = () => {
  try { return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') }; } catch { return { ...DEFAULT_PREFS }; }
};

const Toggle = ({ on, onChange, testId }) => (
  <button
    data-testid={testId}
    role="switch"
    aria-checked={on}
    onClick={() => onChange(!on)}
    className={`relative w-12 h-7 rounded-full shrink-0 transition-colors ${on ? 'bg-[#EE3B36]' : 'bg-gray-300'}`}
  >
    <span className={`absolute top-0.5 left-0 w-6 h-6 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
  </button>
);

const SwitchRow = ({ icon: Icon, title, sub, on, onChange, testId }) => (
  <div className="w-full flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-3">
    <div className="w-10 h-10 rounded-xl bg-[#FDECEC] text-[#EE3B36] flex items-center justify-center shrink-0"><Icon size={20} /></div>
    <div className="flex-1 min-w-0"><p className="text-sm font-semibold">{title}</p><p className="text-xs text-gray-500">{sub}</p></div>
    <Toggle on={on} onChange={onChange} testId={testId} />
  </div>
);

const LinkRow = ({ icon: Icon, title, sub, onClick, testId, danger = false }) => (
  <button data-testid={testId} onClick={onClick} className={`w-full flex items-center gap-3 bg-white border rounded-xl p-3 text-left hover:bg-gray-50 ${danger ? 'border-red-100 text-red-600' : 'border-gray-100'}`}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${danger ? 'bg-red-50' : 'bg-[#FDECEC] text-[#EE3B36]'}`}><Icon size={20} /></div>
    <div className="flex-1 min-w-0"><p className="text-sm font-semibold">{title}</p>{sub && <p className="text-xs text-gray-500">{sub}</p>}</div>
    <ChevronRight size={18} className="text-gray-400" />
  </button>
);

// ------------------------------------------------------------------ Mis datos
export const MyData = () => {
  const { customer, setCustomer, customerRecord, updateCustomer, changePassword } = useApp();
  const [form, setForm] = useState({
    fullName: customer?.fullName || '',
    phone: customer?.phone || '',
    email: customer?.email || '',
  });
  const [pwd, setPwd] = useState({ current: '', next: '', repeat: '' });
  const [busy, setBusy] = useState(false);
  const [busyPwd, setBusyPwd] = useState(false);

  const save = async () => {
    const fullName = form.fullName.trim();
    if (fullName.length < 3) return toast.error('Escribe tu nombre completo');
    if (form.phone.replace(/\D/g, '').length < 8) return toast.error('El teléfono debe tener 8 dígitos');
    setBusy(true);
    try {
      const patch = { name: fullName, phone: form.phone.trim(), email: form.email.trim() };
      if (customerRecord?.id) await updateCustomer(customerRecord.id, patch);
      setCustomer({ ...customer, fullName, name: fullName.split(' ')[0], phone: patch.phone, email: patch.email });
      toast.success('Tus datos fueron actualizados');
    } catch (e) { /* the context already shows the error toast */ }
    setBusy(false);
  };

  const savePassword = async () => {
    if (pwd.next.length < 6) return toast.error('La nueva contraseña debe tener al menos 6 caracteres');
    if (pwd.next !== pwd.repeat) return toast.error('Las contraseñas no coinciden');
    setBusyPwd(true);
    try {
      await changePassword(pwd.current, pwd.next);
      setPwd({ current: '', next: '', repeat: '' });
    } catch (e) { /* the context already shows the error toast */ }
    setBusyPwd(false);
  };

  return (
    <Screen>
      <TopBar title="Mis datos" />
      <div className="px-4 pt-2 pb-6" data-testid="my-data-screen">
        <div className="flex items-center gap-4 bg-white border border-gray-100 rounded-2xl p-4">
          <div className="bg-[#EE3B36] rounded-full p-0.5">
            <AvatarPicker testId="my-data-avatar" value={customer?.avatar} folder="avatars" onChange={(url) => { setCustomer({ ...customer, avatar: url }); if (customerRecord?.id) updateCustomer(customerRecord.id, { avatar: url }); }} />
          </div>
          <div className="min-w-0">
            <p className="font-extrabold text-[15px] truncate">{customer?.fullName}</p>
            <p className="text-xs text-gray-500">Toca la foto para cambiarla</p>
          </div>
        </div>

        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mt-5 mb-2 px-1">Datos personales</p>
        <div className="space-y-2">
          <MobileInput data-testid="my-data-name" icon={User} label="Nombre completo" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} placeholder="Tu nombre y apellido" />
          <MobileInput data-testid="my-data-phone" icon={Phone} label="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="9999-9999" inputMode="tel" />
          <MobileInput data-testid="my-data-email" icon={Mail} label="Correo electrónico (opcional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="tucorreo@ejemplo.com" inputMode="email" />
        </div>
        <PrimaryButton data-testid="my-data-save" onClick={save} disabled={busy} className="mt-4">{busy ? 'Guardando...' : 'Guardar cambios'}</PrimaryButton>

        {customer?.hasPassword && (
          <>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mt-7 mb-2 px-1">Cambiar contraseña</p>
            <div className="space-y-2">
              <MobileInput data-testid="my-data-pwd-current" icon={Lock} label="Contraseña actual" type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} placeholder="••••••" />
              <MobileInput data-testid="my-data-pwd-new" icon={Lock} label="Nueva contraseña" type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} placeholder="Mínimo 6 caracteres" />
              <MobileInput data-testid="my-data-pwd-repeat" icon={Lock} label="Repite la nueva contraseña" type="password" value={pwd.repeat} onChange={(e) => setPwd({ ...pwd, repeat: e.target.value })} placeholder="Mínimo 6 caracteres" />
            </div>
            <PrimaryButton data-testid="my-data-pwd-save" variant="dark" onClick={savePassword} disabled={busyPwd} className="mt-4">{busyPwd ? 'Actualizando...' : 'Actualizar contraseña'}</PrimaryButton>
          </>
        )}
      </div>
    </Screen>
  );
};

// ----------------------------------------------------- Notificaciones (prefs)
export const NotificationSettings = () => {
  const navigate = useNavigate();
  const { customerUnread } = useApp();
  const [prefs, setPrefs] = useState(notifPrefs);
  const [muted, setMutedState] = useState(isMuted);

  const set = (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  };

  return (
    <Screen>
      <TopBar title="Notificaciones" />
      <div className="px-4 pt-2 pb-6" data-testid="notification-settings-screen">
        <LinkRow icon={Smartphone} testId="notif-open-inbox" title="Ver mis notificaciones" sub={customerUnread > 0 ? `${customerUnread} sin leer` : 'Bandeja de entrada'} onClick={() => navigate('/app/notificaciones')} />

        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mt-5 mb-2 px-1">Qué quieres recibir</p>
        <div className="space-y-2">
          <SwitchRow icon={ShoppingBag} testId="notif-toggle-orders" title="Estado de mis pedidos" sub="Pago verificado, en camino y entregado" on={prefs.orders} onChange={(v) => set('orders', v)} />
          <SwitchRow icon={Bike} testId="notif-toggle-driver" title="Mensajes del repartidor" sub="Llamadas y chat durante la entrega" on={prefs.driver} onChange={(v) => set('driver', v)} />
          <SwitchRow icon={Megaphone} testId="notif-toggle-promos" title="Promociones y ofertas" sub="Descuentos y envíos gratis" on={prefs.promos} onChange={(v) => set('promos', v)} />
        </div>

        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mt-5 mb-2 px-1">Sonido</p>
        <SwitchRow icon={Volume2} testId="notif-toggle-sound" title="Sonido de alerta" sub={muted ? 'Silenciado' : 'Suena al llegar un aviso'} on={!muted} onChange={(v) => { setMuted(!v); setMutedState(!v); }} />

        <p className="text-[11.5px] text-gray-400 mt-5 leading-relaxed">Los avisos del estado de tu pedido siempre aparecen dentro de la app. Estas opciones controlan las notificaciones del teléfono y el sonido.</p>
      </div>
    </Screen>
  );
};

// -------------------------------------------------- Configuración de cuenta
export const AccountSettings = () => {
  const navigate = useNavigate();
  const { customerUnread } = useApp();
  return (
    <Screen>
      <TopBar title="Configuración de cuenta" />
      <div className="px-4 pt-2 pb-6" data-testid="account-settings-screen">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 px-1">Idioma</p>
        <div className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FDECEC] text-[#EE3B36] flex items-center justify-center shrink-0"><Languages size={20} /></div>
          <div className="flex-1 min-w-0"><p className="text-sm font-semibold">Idioma de la app</p><p className="text-xs text-gray-500">Español o inglés</p></div>
          <LanguageSwitch />
        </div>

        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mt-5 mb-2 px-1">Cuenta</p>
        <div className="space-y-2">
          <LinkRow icon={User} testId="settings-my-data" title="Mis datos" sub="Nombre, teléfono y contraseña" onClick={() => navigate('/app/datos')} />
          <LinkRow icon={Smartphone} testId="settings-notifications" title="Notificaciones" sub={customerUnread > 0 ? `${customerUnread} sin leer` : 'Pedidos, repartidor y promociones'} onClick={() => navigate('/app/ajustes/notificaciones')} />
        </div>

        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mt-5 mb-2 px-1">Privacidad</p>
        <div className="space-y-2">
          <LinkRow icon={ShieldCheck} testId="settings-privacy" title="Política de privacidad" sub="Qué datos guardamos y por qué" onClick={() => navigate('/app/legal/privacidad')} />
          <LinkRow icon={FileText} testId="settings-terms" title="Términos y condiciones" sub="Reglas del servicio" onClick={() => navigate('/app/legal/terminos')} />
          <DeleteAccountButton />
        </div>

        <p data-testid="settings-version" className="text-center text-[11.5px] text-gray-400 mt-6">Delibery 7 · versión {APP_VERSION}</p>
      </div>
    </Screen>
  );
};

// ------------------------------------------- Legal pages inside the phone UI
export const LegalScreen = () => {
  const { slug } = useParams();
  const page = PAGES[slug] || PAGES.privacidad;
  return (
    <Screen>
      <TopBar title={page.title} />
      <div className="px-4 pt-1 pb-6 legal-in-app" data-testid={`in-app-${page.key}`}>
        {page.body}
      </div>
    </Screen>
  );
};
